import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useSEO } from '@/components/feature/useSEO';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categoryId === currentCategory.id);

  useSEO({
    title: `${currentCategory.name} - 相关文章与指南`,
    description: `${currentCategory.description}。浏览一起草${currentCategory.name}相关的协同创作文章、教程与最佳实践。`,
    keywords: `${currentCategory.name},一起草,协同编辑,文稿协作,${currentCategory.name}模板`,
    canonicalUrl: `https://utshealth.cn/category/${currentCategory.slug}`,
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': `${currentCategory.name} - 一起草`,
      'description': currentCategory.description,
      'url': `https://utshealth.cn/category/${currentCategory.slug}`,
      'isPartOf': {
        '@type': 'WebSite',
        'name': '一起草',
        'url': 'https://utshealth.cn',
      },
    },
  });

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '分类', to: '/category/hetong-wenshu' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-10">
            <div className={`inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-gradient-to-r ${currentCategory.gradient} text-background-50 mb-4`}>
              <i className={`${currentCategory.icon} text-xl`}></i>
              <span className="text-lg font-heading font-bold">{currentCategory.name}</span>
            </div>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10 pb-6 border-b border-background-200/70 overflow-x-auto">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  cat.slug === currentCategory.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={article.cardImage}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {article.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.author}</span>
                    <span>{article.publishDate} · {article.readTime}分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}